import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';

import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { allTravelDnaSteps } from '@/_constants/TravelDnaStep.ts';
import TravelDnaStep from '@/_constants/TravelDnaStep.ts';
import FoodPreference, {
  allFoodPreferences,
} from '$/_const/dna/FoodPreference.ts';
import { CONSENT_POLICY_VERSION } from '$/_const/dna/ConsentPolicyVersion.ts';
import type { TravelDnaFormData } from '$/models/Dna.d.ts';
import type { OnCompleteArgs } from '@/components/functional/TravelDnaManipulationForm/TravelDnaManipulationForm.tsx';
import DnaStepLayout from '@/components/functional/TravelDnaManipulationForm/shared/DnaStepLayout.tsx';
import DnaPill from '@/components/functional/TravelDnaManipulationForm/shared/DnaPill.tsx';
import DnaPrimaryButton from '@/components/functional/TravelDnaManipulationForm/shared/DnaPrimaryButton.tsx';
import DnaError from '@/components/functional/TravelDnaManipulationForm/shared/DnaError.tsx';
import { ConsentComponent } from '@/components/functional/Consent/Consent.tsx';

const yourFoodPreferencesSchema = z
  .object({
    food_preferences: z.array(z.enum(allFoodPreferences)),
    food_allergy_details: z.string().trim().max(1000).nullable(),
    food_preferences_custom: z.string().trim().max(1000).nullable(),
    userConsent: z.boolean(),
  })
  .superRefine((data, context) => {
    if (data.food_preferences.length > 0 && !data.userConsent) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['userConsent'],
        message: 'Please confirm that Journi may use this information when planning your trips.',
      });
    }

    if (
      data.food_preferences.includes(FoodPreference.ALLERGY) &&
      !data.food_allergy_details
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['food_allergy_details'],
        message: 'Please complete the selected free-text field.',
      });
    }

    if (
      data.food_preferences.includes(FoodPreference.OTHER_PREFERENCE) &&
      !data.food_preferences_custom
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['food_preferences_custom'],
        message: 'Please complete the selected free-text field.',
      });
    }
  });

type YourFoodPreferencesForm = z.infer<typeof yourFoodPreferencesSchema>;
export type YourFoodPreferencesSchema = Omit<YourFoodPreferencesForm, 'userConsent'>;

type YourFoodPreferencesProps = {
  onComplete?: (args: OnCompleteArgs) => Promise<void>;
  onCompleteWhenDirty?: (args: OnCompleteArgs) => Promise<void>;
};

function YourFoodPreferences({
  onComplete,
  onCompleteWhenDirty,
}: YourFoodPreferencesProps) {
  const { t } = useTranslation();
  const { nextStep, setContextData, contextData } = useStepperContext<
    typeof allTravelDnaSteps,
    TravelDnaFormData
  >();
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const critical = contextData?.critical_requirements;

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    clearErrors,
    formState: { errors, isDirty },
  } = useForm<YourFoodPreferencesForm>({
    resolver: zodResolver(yourFoodPreferencesSchema),
    defaultValues: {
      food_preferences: critical?.food_preferences ?? [],
      food_allergy_details: critical?.food_allergy_details ?? '',
      food_preferences_custom: critical?.food_preferences_custom ?? '',
      userConsent: false,
    },
  });

  const selected = watch('food_preferences') ?? [];
  const showAllergy = selected.includes(FoodPreference.ALLERGY);
  const showOther = selected.includes(FoodPreference.OTHER_PREFERENCE);
  const userConsent = watch('userConsent');

  const onSubmit = (data: YourFoodPreferencesForm) => {
    void (async () => {
      try {
        setIsPending(true);
        setError(null);
        const cleanData: YourFoodPreferencesSchema = {
          food_preferences: data.food_preferences,
          food_allergy_details: showAllergy
            ? data.food_allergy_details
            : null,
          food_preferences_custom: showOther
            ? data.food_preferences_custom
            : null,
        };

        const context: TravelDnaFormData = {
          ...contextData,
          critical_requirements: {
            ...contextData?.critical_requirements,
            ...cleanData,
          },
        };
        const args: OnCompleteArgs = {
          step: TravelDnaStep.YOUR_FOOD_PREFERENCES,
          consent: {
            page: TravelDnaStep.YOUR_FOOD_PREFERENCES,
            version: CONSENT_POLICY_VERSION,
            status: data.userConsent ? 'granted' : 'withdrawn',
          },
          data: cleanData,
          context,
        };

        if (isDirty || !critical) {
          setContextData(() => context);
          await onCompleteWhenDirty?.(args);
        }

        await onComplete?.(args);
        nextStep();
      } catch (caughtError) {
        setError(
          caughtError instanceof Error
            ? caughtError
            : new Error(String(caughtError))
        );
      } finally {
        setIsPending(false);
      }
    })();
  };

  return (
    <DnaStepLayout
      eyebrow={t('travelDnaPage.yourFoodPreferences.header')}
      title={t('travelDnaPage.yourFoodPreferences.subheader')}
      description={`${t('travelDnaPage.yourFoodPreferences.description')}\n${t(
        'travelDnaPage.yourFoodPreferences.descriptionSecond'
      )}`}
      maxWidthClassName="max-w-[970px]"
    >
      <form className="mt-8 flex w-full flex-col items-center" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DnaError error={error} className="mb-6" />

        <Controller
          control={control}
          name="food_preferences"
          render={({ field }) => (
            <div className="flex w-full flex-wrap justify-center gap-4 sm:gap-6">
              {allFoodPreferences.map((option) => {
                const isSelected = field.value.includes(option);
                const toggle = () => {
                  const nextSelected = isSelected ? field.value.filter((item) => item !== option) : [...field.value, option];
                  field.onChange(nextSelected);
                  if (nextSelected.length === 0) clearErrors('userConsent');
                };

                return (
                  <DnaPill
                    key={option}
                    label={t(`travelDnaPage.yourFoodPreferences.options.${option}`)}
                    selected={isSelected}
                    disabled={isPending}
                    onClick={toggle}
                  />
                );
              })}
            </div>
          )}
        />

        <div className="mt-12 grid w-full gap-8 text-left">
          {showAllergy ? (
            <label className="font-sans text-base text-[var(--cream)]">
              {t('travelDnaPage.yourFoodPreferences.allergyLabel')} <span className="text-[var(--gold)]">*</span>
              <input
                {...register('food_allergy_details')}
                type="text"
                className="mt-2 h-12 w-full rounded-[9px] border border-[var(--gold-deep)] bg-[var(--gold-deep)] px-5 font-sans text-base text-white outline-none placeholder:text-white/80 focus:border-[var(--gold-lt)] focus:ring-2 focus:ring-[var(--gold)]/30"
                placeholder={t('travelDnaPage.yourFoodPreferences.allergyPlaceholder')}
              />
              {errors.food_allergy_details ? (
                <span className="mt-2 block text-sm text-error">
                  {t('travelDnaPage.yourFoodPreferences.validation')}
                </span>
              ) : null}
            </label>
          ) : null}

          {showOther ? (
            <label className="font-sans text-base text-[var(--cream)]">
              {t('travelDnaPage.yourFoodPreferences.otherLabel')} <span className="text-[var(--gold)]">*</span>
              <input
                {...register('food_preferences_custom')}
                type="text"
                className="mt-2 h-12 w-full rounded-[9px] border border-[var(--gold-deep)] bg-[var(--gold-deep)] px-5 font-sans text-base text-white outline-none placeholder:text-white/80 focus:border-[var(--gold-lt)] focus:ring-2 focus:ring-[var(--gold)]/30"
                placeholder={t('travelDnaPage.yourFoodPreferences.otherPlaceholder')}
              />
              {errors.food_preferences_custom ? (
                <span className="mt-2 block text-sm text-error">
                  {t('travelDnaPage.yourFoodPreferences.validation')}
                </span>
              ) : null}
            </label>
          ) : null}
          <div>
            <ConsentComponent
              setConsent={(checked) =>
                setValue('userConsent', checked, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              userConsent={userConsent}
              invalid={Boolean(errors.userConsent)}
              disabled={isPending}
            />

          </div>
        </div>

        <DnaPrimaryButton className="mt-16" loading={isPending} type="submit">
          {t('travelDnaPage.yourFoodPreferences.continue')}
        </DnaPrimaryButton>
      </form>
    </DnaStepLayout>
  );
}

export default YourFoodPreferences;
